<!-- Copyright (c) 2020-2023 ForgeRock. All rights reserved.
This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details. -->
<template>
  <div>
    <FrListGroup :title="this.$t('pages.workflow.myTasks')">
      <div class="toprow">
        <div class="topBarInput">
          <p>{{ " For bulk approval/rejection please add comments" }}</p>
          <BFormGroup style="width:90%"
            :label="Comments"
            label-for="bulkComments"
            horizontal
            :key="comments"
            >
            <BFormInput
              horizontal
              type="text"
              :plaintext="false"
              :name="comments"
              aria-placeholder="Comments"
              :value="fieldvalue"
              v-model="fieldvalue" />
          </BFormGroup>
        </div>

        <div class="mt-4 mytaskButtons">
          <BButton Button
            class="mr-2 rejectButton"
            v-bind:disabled="buttonDisabled"
            @click="bulkReject"
            >
            {{ "Reject" }}
          </BButton>
          <BButton Button
            @click="bulkApprove"
            v-bind:disabled="buttonDisabled"
            class="mr-2 rejectButton"
            variant="primary">
            {{ "Accept" }}
          </BButton>
        </div>
      </div>
     <div v-if="!isEmpty(categorizedTasks.bulkApprovable) || !isEmpty(categorizedTasks.normal) ">
      <template v-if="!isEmpty(categorizedTasks.bulkApprovable)">
      <h3 class="task-section-header"> Bulk Approvable Tasks</h3>
      
        <TransitionGroup
          name="fade"
          mode="out-in">
	  
          <FrListItem
            :collapsible="true"
            v-for="(task, id) in categorizedTasks.bulkApprovable"
            :key="`bulk_${id}`"
            :ref="`collapse-${id}`"
            @shown="setShown(id)"
            @hidden="setHidden(id)">
            <div
              slot="list-item-header"
              class="d-inline-flex w-100 media">
              <div class="media-body align-self-center">
                <h6>{{ task.name }}</h6>
              </div>
              <div class="d-flex flex-row ml-2 align-self-center">
                <BButton
                  v-if="!isEmpty(task.task.candidates.candidateGroups)"
                  variant="link"
                  size="sm"
                  @click.stop="requeue(id)">
                  {{ $t('pages.workflow.requeue') }}
                </BButton>
                <BButton
                  v-if="panelShown[id] === true"
                  variant="link"
                  size="sm"
                  :ref="`cancel-${id}`"
                  class="btn-edit pb-2">
                  {{ $t('common.cancel' ) }}
                </BButton>
                <BButton
                  v-else
                  variant="link"
                  size="sm"
                  class="btn-edit">
                  {{ $t('common.edit' ) }}
                </BButton>
              </div>
            </div>
            <div
              slot="list-item-collapse-body"
              class="d-inline-flex w-100">
              <FrTask
                :task-instance="task"
                :shown="panelShown[id]"
                :ref="id"
                @loadProcess="(process) => $emit('loadProcess', process)"
                @cancel="cancel"
                @completeTask="completeTask" />
            </div>
          </FrListItem>
        </TransitionGroup>
        </template>
        <template v-if="!isEmpty(categorizedTasks.normal)">
        <h3 class="task-section-header">Non-Bulk Approvable Tasks</h3>
      
        <TransitionGroup
          name="fade"
          mode="out-in">
	  
          <FrListItem
            :collapsible="true"
            v-for="(task, id) in categorizedTasks.normal"
            :key="`bulk_${id}`"
            :ref="`collapse-${id}`"
            @shown="setShown(id)"
            @hidden="setHidden(id)">
            <div
              slot="list-item-header"
              class="d-inline-flex w-100 media">
              <div class="media-body align-self-center">
                <h6>{{ task.name }}</h6>
              </div>
              <div class="d-flex flex-row ml-2 align-self-center">
                <BButton
                  v-if="!isEmpty(task.task.candidates.candidateGroups)"
                  variant="link"
                  size="sm"
                  @click.stop="requeue(id)">
                  {{ $t('pages.workflow.requeue') }}
                </BButton>
                <BButton
                  v-if="panelShown[id] === true"
                  variant="link"
                  size="sm"
                  :ref="`cancel-${id}`"
                  class="btn-edit pb-2">
                  {{ $t('common.cancel' ) }}
                </BButton>
                <BButton
                  v-else
                  variant="link"
                  size="sm"
                  class="btn-edit">
                  {{ $t('common.edit' ) }}
                </BButton>
              </div>
            </div>
            <div
              slot="list-item-collapse-body"
              class="d-inline-flex w-100">
              <FrTask
                :task-instance="task"
                :shown="panelShown[id]"
                :ref="id"
                @loadProcess="(process) => $emit('loadProcess', process)"
                @cancel="cancel"
                @completeTask="completeTask" />
            </div>
          </FrListItem>
        </TransitionGroup>
        </template>
      </div>  

      <FrListItem v-else>
        <div
          slot="list-item-header"
          class="text-muted text-center w-100">
          {{ $t('pages.workflow.noAssignedTasks') }}
        </div>
      </FrListItem>
    </FrListGroup>
  </div>
</template>

<script>
import {
  isFunction, isEmpty, keys, difference, first, isUndefined, forEach,
} from 'lodash';
import { mapState, mapActions } from 'vuex';
import FrListGroup from '@forgerock/platform-shared/src/components/ListGroup/';
import FrListItem from '@forgerock/platform-shared/src/components/ListItem/';
import Task from '../Task';
import NotificationMixin from '@forgerock/platform-shared/src/mixins/NotificationMixin';
/**
* @description Dashboard widget that lists tasks currently assigned to the logged in user
*
* */
export default {
  name: 'MyTasks',
  mixins: [
    NotificationMixin,
  ],
  props: {
    tasks: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapState(['buttonDisabled']),
  categorizedTasks() {
    let categorized = {
      bulkApprovable: [],
      normal: [],
    };
    Object.entries(this.tasks).forEach(([taskId, taskData]) => {
      
      if (taskData.task.variables && (taskData.task.variables.customTask||taskData.task.variables.sapUserGroup)) {
        categorized.normal.push({ id: taskId, ...taskData });
      } else {
        categorized.bulkApprovable.push({ id: taskId, ...taskData });
      }
    });
    
    return categorized;
  },
  
},
  data() {
    const panelShown = {};
    forEach(this.tasks, (value, id) => {
      panelShown[id] = false;
    });
    return {
      // buttonDisabled : false,
      panelShown,
      onHidden: null,
      fieldvalue:"",
      comments: "",
      Comments: "",
    };
  },
  components: {
    FrListGroup,
    FrListItem,
    FrTask: Task,
  },
  methods: {
    isEmpty,
    first,
    ...mapActions(['disableButton','enableButton']),
    setShown(id) {
      this.$set(this.panelShown, id, true);
    },
    setHidden(id) {
      this.$set(this.panelShown, id, false);
      if (isFunction(this.onHidden)) {
        this.onHidden();
        this.onHidden = null;
      }
    },
    cancel(id) {
      first(this.$refs[`cancel-${id}`]).click();
    },
    requeue(id) {
      const { task } = this.tasks[id];
      const action = 'updateAssignment';
      const payload = { id, task, assignee: null };
      this.update(id, action, payload);
    },
    completeTask(payload) {
      this.update(payload.id, 'completeTask', payload);
    },
    // setTimeoutBulkReject(){
    //   setTimeout( this.bulkReject,2000)
    // },
    // setTimeoutBulkApprove(){
    //   setTimeout(this.bulkApprove,2000)
    // },
    async bulkReject(){
      //this.buttonDisabled = true;
      if(this.fieldvalue === ""){
        this.displayNotification('danger', "Please enter bulk rejection comment");
      } else{
          this.$store.commit('disableButton');
          //var dataPreparation = this.tasks
          //for (var value in dataPreparation){
            //var taskId = value
            //var dataValue = dataPreparation[value]
            for (const task of this.categorizedTasks.bulkApprovable) {
            const taskId = task.id;
            const dataValue = task;
	                var bulkPayloadValue = {
                                      "id": taskId,
                                      "formData":{
                                        "reqtrName": dataValue.task.variables.reqtrName,
                                        "reqtrEmail": dataValue.task.variables.reqtrEmail,
                                        "organization": dataValue.task.variables.organization,
                                        "emailDomains": dataValue.task.variables.emailDomains,
                                        "address": dataValue.task.variables.address,
                                        "country": dataValue.task.variables.country,
                                        "state": dataValue.task.variables.state,
                                        "city": dataValue.task.variables.city,
                                        "zip": dataValue.task.variables.zip,
                                        "telephoneNumber": dataValue.task.variables.telephoneNumber,
                                        "countryOfIncorporation": dataValue.task.variables.countryOfIncorporation,
                                        "typeOfCompany":dataValue.task.variables.typeOfCompany,
                                        "description": dataValue.task.variables.description,
                                        "currentAffiliations": dataValue.task.variables.currentAffiliations,
                                        "sponsor": dataValue.task.variables.sponsor,
                                        "engineModels": dataValue.task.variables.engineModels,
                                        "portals": dataValue.task.variables.portals,
                                        "companyOfficerName": dataValue.task.variables.companyOfficerName,
                                        "companyOfficerEmail": dataValue.task.variables.companyOfficerEmail,
                                        "companyOfficerPhone": dataValue.task.variables.companyOfficerPhone,
                                        "secondaryAdminName": dataValue.task.variables.secondaryAdminName,
                                        "secondaryAdminEmail": dataValue.task.variables.secondaryAdminEmail,
                                        "secondaryAdminPhone": dataValue.task.variables.secondaryAdminPhone,
                                        "appNames": dataValue.task.variables.appNames,
                                        "comments": this.fieldvalue,
                                        "decision": 'reject',
                                        },
                                    };
        await new Promise (resolve => setTimeout(()=>
                {
                  this.update(taskId, 'completeTask', bulkPayloadValue);
                  resolve();
                },1000));
           
          
      }
      this.fieldvalue= '';
      }
    },
    async bulkApprove(){
      //this.buttonDisabled = true;
      
      if(this.fieldvalue === ""){
        this.displayNotification('danger', "Please enter bulk approval comment");
      } else{
          this.$store.commit('disableButton');
          //var dataPreparation = this.tasks
          //for (var value in dataPreparation){
            //var taskId = value
            //var dataValue = dataPreparation[value]
            for (const task of this.categorizedTasks.bulkApprovable) {
          const taskId = task.id;
          const dataValue = task;
            var bulkPayloadValue = {
                                    "id": taskId,
                                "formData":{
                                  "reqtrName": dataValue.task.variables.reqtrName,
                                  "reqtrEmail": dataValue.task.variables.reqtrEmail,
                                  "organization": dataValue.task.variables.organization,
                                  "emailDomains": dataValue.task.variables.emailDomains,
                                  "address": dataValue.task.variables.address,
                                  "country": dataValue.task.variables.country,
                                  "state": dataValue.task.variables.state,
                                  "city": dataValue.task.variables.city,
                                  "zip": dataValue.task.variables.zip,
                                  "telephoneNumber": dataValue.task.variables.telephoneNumber,
                                  "countryOfIncorporation": dataValue.task.variables.countryOfIncorporation,
                                  "typeOfCompany":dataValue.task.variables.typeOfCompany,
                                  "description": dataValue.task.variables.description,
                                  "currentAffiliations": dataValue.task.variables.currentAffiliations,
                                  "sponsor": dataValue.task.variables.sponsor,
                                  "engineModels": dataValue.task.variables.engineModels,
                                  "portals": dataValue.task.variables.portals,
                                  "companyOfficerName": dataValue.task.variables.companyOfficerName,
                                  "companyOfficerEmail": dataValue.task.variables.companyOfficerEmail,
                                  "companyOfficerPhone": dataValue.task.variables.companyOfficerPhone,
                                  "secondaryAdminName": dataValue.task.variables.secondaryAdminName,
                                  "secondaryAdminEmail": dataValue.task.variables.secondaryAdminEmail,
                                  "secondaryAdminPhone": dataValue.task.variables.secondaryAdminPhone,
                                  "appNames": dataValue.task.variables.appNames,
                                  "comments": this.fieldvalue,
                                  "decision": 'accept',
                                  },
                              };
      await new Promise (resolve => setTimeout(()=>{
                                                    this.update(taskId, 'completeTask', bulkPayloadValue);
                                                    resolve();
                                                  },1000));
        
      }
      this.fieldvalue= '';
}
    },
    update(id, action, payload) {
      const update = () => {
        this.$emit(action, payload);
      };
      if (this.panelShown[id]) {
        this.onHidden = update;
        this.cancel(id);
      } else {
        update();
      }
    },
  },
  watch: {
    tasks: {
      /**
        * This function sets the state of panelShown. Anytime new tasks are added to the tasks prop,
        * the prop key is added to the panelShown object with an initial state of `false`.
        */
      handler(val, oldVal) {
        const newVals = difference(keys(val), keys(oldVal));
        if (isUndefined(this.panelShown)) {
          this.panelShown = {};
        }
        newVals.forEach((panelValue) => {
          this.$set(this.panelShown, panelValue, false);
        });
      },
      deep: true,
    },
  },
    
  };
</script>
<style>
.task-section-header {
  padding-top: 20px; 
  margin-bottom: 10px; 
  font-size: 1.5rem; 
  font-weight: bold; 
  color: #333; 
  margin-left: 20px;
}
.task-item {
  padding-top: 10px; 
  padding-bottom: 10px; 
  border-bottom: 1px solid #eaeaea; 
}
.toprow{
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-content: center;
  align-items: center;
  flex-wrap: wrap;
  /* margin:10px; */
  margin-left:25px;
  margin-bottom:10px;
  margin-right:10px;
  margin-top:10px;
  height: 125px;
}
.mytaskButtons{
  /* padding-bottom:27px; */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-content: center;
  align-items: center;
  height: 50px;
  padding-bottom: 19px;
}
.topBarInput{
  height: 50px;
  width: 76%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  
}
.rejectButton{
  background-color: white;
  border: 1px solid #F7685B;
  color: #F7685B;
  height: 48.5px;
  min-width:81.5px;
}
.rejectButton:hover{
  background-color: #F7685B;
  border: 1px solid white;
  color: white;
}
</style>